import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import "@/App.css";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Sparkles,
  Radar,
  BookOpen,
  Brain,
  ArrowRight,
  ShieldCheck,
  TrendingUp,
  Zap,
  AlertCircle,
  X,
  MoreVertical,
  MoreHorizontal,
  Star,
  Calendar,
  MapPin,
  MessageCircle,
  BarChart3,
} from "lucide-react";

const INVITE_HASH = "m-9X6w2COwsyYTE1";
const TELEGRAM_LINK = `https://t.me/+${INVITE_HASH}`;
const TELEGRAM_DEEPLINK = `tg://join?invite=${INVITE_HASH}`;

// ---- Integrasi CRM UnderAds Marketing Hub ----
// Token dijana oleh CRM (?t= baharu, ?test_id= alias untuk link lama).
// LP tidak menjana token atau invite sendiri.
const CRM_PUBLIC_ORIGIN = process.env.REACT_APP_CRM_PUBLIC_ORIGIN;
const TRACKING_TOKEN_RE = /^[A-Za-z0-9_-]{20,64}$/;

const resolveTrackingToken = () => {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const tValues = params.getAll("t");
    const legacyValues = params.getAll("test_id");
    // Parameter berulang -> CTA umum, tiada atribusi
    if (tValues.length > 1 || legacyValues.length > 1) return null;
    const t = tValues.length === 1 ? tValues[0] : null;
    const legacy = legacyValues.length === 1 ? legacyValues[0] : null;
    // Nilai hadir tetapi kosong/salah format -> CTA umum
    if (t !== null && !TRACKING_TOKEN_RE.test(t)) return null;
    if (legacy !== null && !TRACKING_TOKEN_RE.test(legacy)) return null;
    // Kedua-duanya sah: mesti sama, jika bercanggah -> CTA umum
    if (t && legacy) return t === legacy ? t : null;
    return t || legacy || null;
  } catch {
    return null;
  }
};

// Diselesaikan sekali semasa load — token kekal dalam URL sepanjang lawatan
// (refresh & navigasi dalaman SPA tidak membuang query string).
const TRACKING_TOKEN = resolveTrackingToken();

// Satu helper untuk SEMUA CTA Telegram: token sah + origin lengkap ->
// endpoint CRM (yang akan 302 ke invite Telegram). Selain itu -> link umum.
const getTelegramUrl = () => {
  if (TRACKING_TOKEN && CRM_PUBLIC_ORIGIN) {
    return `${CRM_PUBLIC_ORIGIN}/api/media-buying/public/telegram/${encodeURIComponent(TRACKING_TOKEN)}`;
  }
  return TELEGRAM_LINK;
};
const LOGO_BLACK =
  "https://customer-assets.emergentagent.com/job_3c804a5e-7f59-4fa6-b1b1-925d9e68b9a1/artifacts/j8f4aoko_FIRA%20INDICATOR%20BLACK.png";

const TESTIMONIALS = [
  {
    src: "https://customer-assets.emergentagent.com/job_3c804a5e-7f59-4fa6-b1b1-925d9e68b9a1/artifacts/8ispec48_100%25%20FOLLOW%20SMART%20DESK.jpg",
    alt: "100% Follow Smart Desk — Student Fira dapat $1k dalam satu malam",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_3c804a5e-7f59-4fa6-b1b1-925d9e68b9a1/artifacts/nfnpn5hr_100%25%20GUNA%20SMART%20DESK.jpg",
    alt: "100% Guna Fira Smart Desk — Student Fira grow account almost 200%",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_3c804a5e-7f59-4fa6-b1b1-925d9e68b9a1/artifacts/3ku884vf_FLIP%20LEBIH%20100%25.jpg",
    alt: "Seminggu Join Fira — Berjaya flip lebih 100%",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_fira-trading-ai/artifacts/n19j1ndg_HASIL%202%20MINGGU%20FOLLOW%20FIRA.jpg",
    alt: "Hasil 2 Minggu Follow Fira — Student berjaya grow & withdraw RM2k++",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_fira-trading-ai/artifacts/8ukuagi4_STUDENT%20100%25%20FOLLOW%20SMART%20DESK.jpg",
    alt: "Student 100% Follow Smart Desk — Hold setup 100 pips & flip account",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_fira-trading-ai/artifacts/q306usd3_STUDENT%20FOLLOW%20RAPAT%20SETUP%20H4.jpg",
    alt: "Student Follow Rapat Setup H4 Fira Smart Desk — Hold gold junam 100 pips",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_fira-trading-ai/artifacts/zbyrpoas_STUDENT%20TRADE%20GUNA%20FSD.jpg",
    alt: "Student Fira Trade Follow Fira Smart Desk — Berjaya grow & withdraw RM6k++",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_fira-trading-ai/artifacts/01djo91q_TRADE%20CONFIDENCE.jpg",
    alt: "Trade Confidence Follow Fira Smart Desk — Grow account sampai RM1k++",
  },
];

const PHONE_MOCKUP =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_fira-trading-ai/artifacts/90fwvv3s_PHONE%20MOCKUP%20FIRA.webp";

const MEET_TRADE_POSTER =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_fira-trading-ai/artifacts/0c2b1ef668ba0fb9_MEET%20AND%20TRADE%20OCTOBER%20WEBSITE.jpg";

const MEET_TRADE_AGENDA = [
  {
    icon: TrendingUp,
    title: "Live Trade",
    desc: "Lihat Fira trade real-time, dari analisa setup sampai execution.",
  },
  {
    icon: MessageCircle,
    title: "Q&A Session",
    desc: "Tanya apa-apa pasal trading — mentorship direct dengan Fira.",
  },
  {
    icon: BarChart3,
    title: "Market Analysis",
    desc: "Breakdown market view & setup terkini untuk minggu depan.",
  },
];

const FEATURES = [
  {
    icon: <Sparkles size={26} strokeWidth={1.6} />,
    title: "AI Signal",
    desc: "Signal dengan confident level untuk bantu tapis setup terbaik tanpa teka-teki.",
  },
  {
    icon: <Radar size={26} strokeWidth={1.6} />,
    title: "AI Market Scanner",
    desc: "Scan peluang market tanpa perlu buka chart satu-satu. Save masa, fokus pada setup.",
  },
  {
    icon: <Brain size={26} strokeWidth={1.6} />,
    title: "AI Fundamental Insight",
    desc: "Bantu faham arah market sebelum entry — dari news ke macro view, semua jelas.",
  },
  {
    icon: <BookOpen size={26} strokeWidth={1.6} />,
    title: "Smart Journal",
    desc: "Analisa performance trading dan kesan pattern sendiri secara automatik.",
  },
];

const trackLead = (location) => {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Lead", { source: location });
    }
  } catch (err) {
    // silent
  }
};

// ---- Internal analytics tracking ----
const getVisitorId = () => {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = window.localStorage.getItem("fira_visitor_id");
    if (!id) {
      id =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem("fira_visitor_id", id);
    }
    return id;
  } catch {
    return `v_${Date.now()}`;
  }
};

// ---- UTM / attribution capture (first-touch, persisted in localStorage) ----
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
];

const getAttribution = () => {
  if (typeof window === "undefined") return {};
  try {
    // Cleanup: remove legacy first-touch attribution left over from older code.
    try {
      window.localStorage.removeItem("fira_attribution");
    } catch {
      // ignore
    }
    const params = new URLSearchParams(window.location.search);
    const fromUrl = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) fromUrl[k] = v.slice(0, 200);
    });
    // Event-level attribution: only UTM present in the CURRENT URL is counted.
    // Return visits without UTM in URL are attributed as "direct".
    return fromUrl;
  } catch {
    return {};
  }
};

const trackEvent = (type, source) => {
  if (typeof window === "undefined") return;
  try {
    const visitorId = getVisitorId();
    const attribution = getAttribution();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/track/event`;
    const body = JSON.stringify({
      type,
      visitor_id: visitorId,
      source: source || null,
      page: window.location.pathname,
      referrer: document.referrer || null,
      utm_source: attribution.utm_source || null,
      utm_medium: attribution.utm_medium || null,
      utm_campaign: attribution.utm_campaign || null,
      utm_content: attribution.utm_content || null,
      utm_term: attribution.utm_term || null,
      fbclid: attribution.fbclid || null,
    });
    // Use sendBeacon for fire-and-forget on click/unload
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // silent
  }
};

// ---- In-app browser detection ----
const detectEnv = () => {
  if (typeof navigator === "undefined") {
    return { inApp: false, source: null, os: null };
  }
  const ua = navigator.userAgent || "";
  let source = null;
  if (/FBAN|FBAV|FB_IAB|FB4A/i.test(ua)) source = "Facebook";
  else if (/Instagram/i.test(ua)) source = "Instagram";
  else if (/TikTok|BytedanceWebview|musical_ly/i.test(ua)) source = "TikTok";
  else if (/Twitter|TwitterAndroid/i.test(ua)) source = "Twitter";
  else if (/Line\//i.test(ua)) source = "Line";
  else if (/MicroMessenger/i.test(ua)) source = "WeChat";
  else if (/Snapchat/i.test(ua)) source = "Snapchat";

  let os = null;
  if (/iPhone|iPad|iPod/i.test(ua)) os = "ios";
  else if (/Android/i.test(ua)) os = "android";

  return { inApp: !!source, source, os };
};

// ---- Telegram opener dengan deep link + fallback ----
// Satu logic untuk semua CTA: token CRM sah -> navigasi biasa ke endpoint
// CRM (bukan fetch/window.open). Tiada token -> deep link sedia ada.
const openTelegram = (sourceTag) => {
  trackLead(sourceTag);
  trackEvent("cta_click", sourceTag);

  if (typeof window === "undefined") return;

  const targetUrl = getTelegramUrl();

  if (targetUrl !== TELEGRAM_LINK) {
    window.location.href = targetUrl;
    return;
  }

  let fallbackTriggered = false;
  let didLeave = false;

  const onVisibility = () => {
    if (document.hidden) didLeave = true;
  };
  document.addEventListener("visibilitychange", onVisibility);

  try {
    window.location.href = TELEGRAM_DEEPLINK;
  } catch (e) {
    // ignore
  }

  setTimeout(() => {
    document.removeEventListener("visibilitychange", onVisibility);
    if (didLeave || fallbackTriggered) return;
    fallbackTriggered = true;
    window.location.href = TELEGRAM_LINK;
  }, 1500);
};

const formatNumber = (n) => n.toLocaleString("en-US");

const CountUp = ({ end, duration = 1800, prefix = "", suffix = "", className }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const startTs = performance.now();
            const tick = (now) => {
              const elapsed = now - startTs;
              const t = Math.min(1, elapsed / duration);
              // easeOutCubic
              const eased = 1 - Math.pow(1 - t, 3);
              setValue(Math.round(eased * end));
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref} className={className} data-testid="count-up">
      {prefix}
      {formatNumber(value)}
      {suffix}
    </span>
  );
};

const CtaButton = ({ label = "Join Telegram", source, testId }) => {
  return (
    <a
      href={getTelegramUrl()}
      target="_blank"
      rel="noopener noreferrer"
      className="btn-cta"
      data-testid={testId}
      onClick={(e) => {
        e.preventDefault();
        openTelegram(source);
      }}
    >
      <span>{label}</span>
      <ArrowRight size={18} strokeWidth={2.4} />
    </a>
  );
};

const Nav = () => {
  const navigate = useNavigate();
  const logoClicksRef = useRef([]);

  const onLogoClick = () => {
    const now = Date.now();
    // Keep only clicks within last 3 seconds
    logoClicksRef.current = logoClicksRef.current.filter((t) => now - t < 3000);
    logoClicksRef.current.push(now);
    if (logoClicksRef.current.length >= 5) {
      logoClicksRef.current = [];
      navigate("/admin/login");
    }
  };

  return (
    <header
      className="w-full fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#08080a]/60 border-b border-white/5"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={LOGO_BLACK}
            alt="Fira"
            onClick={onLogoClick}
            className="logo-invert h-8 w-8 object-contain cursor-pointer select-none"
            data-testid="header-logo"
            draggable={false}
          />
          <span className="font-display font-semibold text-lg tracking-tight">
            Fira <span className="text-[#ff6699]">Smart Desk</span>
          </span>
        </div>
        <a
          href={getTelegramUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#ff6699] text-black font-semibold text-sm hover:bg-[#ff7aa6] transition-colors"
          data-testid="nav-cta-button"
          onClick={(e) => {
            e.preventDefault();
            openTelegram("nav");
          }}
        >
          Join Telegram <ArrowRight size={16} />
        </a>
      </div>
    </header>
  );
};

const Hero = () => (
  <section
    className="relative pt-36 pb-24 overflow-hidden"
    data-testid="hero-section"
  >
    <div className="absolute inset-0 grid-bg" />
    <div
      className="glow-orb"
      style={{
        width: 520,
        height: 520,
        top: -200,
        left: -120,
        background: "#ff6699",
      }}
    />
    <div
      className="glow-orb"
      style={{
        width: 420,
        height: 420,
        bottom: -180,
        right: -100,
        background: "#ff8fb3",
        opacity: 0.3,
      }}
    />
    <div className="noise" />

    {/* floating particles */}
    {Array.from({ length: 6 }).map((_, i) => (
      <span
        key={i}
        className="particle"
        style={{
          top: `${15 + i * 12}%`,
          left: `${(i * 18 + 8) % 95}%`,
          animationDelay: `${i * 1.1}s`,
        }}
      />
    ))}

    <div className="relative max-w-6xl mx-auto px-5 sm:px-8 text-center">
      <div className="reveal" style={{ animationDelay: "0.05s" }}>
        <span className="pill" data-testid="hero-pill">
          <span className="pulse-dot" />
          AI-Powered Trading System · 2026
        </span>
      </div>

      <h1
        className="font-display font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[1.05] mt-7 reveal"
        style={{ animationDelay: "0.18s" }}
        data-testid="hero-headline"
      >
        Unlock Cara Trade <br className="hidden sm:block" />
        Baru Dengan{" "}
        <span className="headline-accent">Fira Smart Desk</span>
      </h1>

      <p
        className="mt-7 max-w-2xl mx-auto text-base sm:text-lg text-white/70 leading-relaxed reveal"
        style={{ animationDelay: "0.3s" }}
        data-testid="hero-subheadline"
      >
        Sistem AI trading yang bantu korang tapis market, pilih setup
        berkualiti dan buat keputusan dengan lebih yakin.
      </p>

      <div
        className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 reveal"
        style={{ animationDelay: "0.42s" }}
      >
        <CtaButton
          label="Join Telegram For 50% Off"
          source="hero"
          testId="hero-cta-button"
        />
        <a
          href="#features"
          className="btn-cta-ghost"
          data-testid="hero-secondary-cta"
        >
          Lihat Features
        </a>
      </div>

      {/* Stats strip */}
      <div
        className="mt-16 grid grid-cols-3 max-w-3xl mx-auto gap-4 sm:gap-8 reveal"
        style={{ animationDelay: "0.55s" }}
      >
        {[
          {
            id: "students",
            num: { end: 35000, suffix: "+" },
            label: "Total Students",
          },
          {
            id: "support",
            staticText: "24/5",
            label: "Live Support",
          },
          {
            id: "signals",
            num: { end: 1200, suffix: "+" },
            label: "Daily AI Signals",
          },
        ].map((s) => (
          <div
            key={s.id}
            className="border border-white/8 rounded-2xl py-5 px-3 bg-white/[0.02] backdrop-blur"
            data-testid={`stat-${s.id}`}
          >
            {s.staticText ? (
              <span
                className="block text-2xl sm:text-3xl font-bold text-[#ff6699] tabular-nums"
                data-testid="count-up"
              >
                {s.staticText}
              </span>
            ) : (
              <CountUp
                end={s.num.end}
                suffix={s.num.suffix}
                className="block text-2xl sm:text-3xl font-bold text-[#ff6699] tabular-nums"
              />
            )}
            <div className="text-xs sm:text-sm text-white/55 mt-1 tracking-wide uppercase">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Tape = () => (
  <div className="tape" data-testid="marquee-tape">
    <div className="tape-inner">
      {Array.from({ length: 2 }).map((_, k) => (
        <span key={k} className="inline-flex items-center gap-12">
          <span>AI Signal</span>
          <span>·</span>
          <span>Market Scanner</span>
          <span>·</span>
          <span>Fundamental Insight</span>
          <span>·</span>
          <span>Smart Journal</span>
          <span>·</span>
          <span>One Smart System</span>
          <span>·</span>
          <span>Fira Smart Desk</span>
          <span>·</span>
        </span>
      ))}
    </div>
  </div>
);

const MeetTrade = () => (
  <section
    id="meet-trade"
    className="relative py-24 sm:py-28 px-5 sm:px-8 overflow-hidden"
    data-testid="meet-trade-section"
  >
    {/* Ambient glow */}
    <div
      className="glow-orb pointer-events-none"
      style={{
        width: 520,
        height: 520,
        top: "15%",
        left: "-10%",
        background: "#ff6699",
        opacity: 0.25,
      }}
    />
    <div
      className="glow-orb pointer-events-none"
      style={{
        width: 380,
        height: 380,
        bottom: "0%",
        right: "-8%",
        background: "#ffb84d",
        opacity: 0.12,
      }}
    />

    <div className="relative max-w-6xl mx-auto">
      <div className="text-center max-w-3xl mx-auto reveal-on-scroll">
        <span className="pill" data-testid="meet-trade-pill">
          <Sparkles size={14} /> Meet &amp; Trade · Coming Soon
        </span>
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-7 leading-[1.05] tracking-tight">
          Meet &amp; <span className="shiny-text">Trade</span>
        </h2>
        <p className="mt-6 text-white/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
          Satu malam eksklusif bersama Fira — eat, sip, trade. Belajar live
          trade, dapat market view, dan network dengan trader serius.{" "}
          <span className="text-[#ff6699] font-semibold">
            Edisi seterusnya coming soon.
          </span>{" "}
          Masuk Telegram channel kami untuk dapat official announcement dulu.
        </p>

        {/* Status badges */}
        <div className="mt-7 inline-flex flex-wrap justify-center items-center gap-2.5">
          <span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#ff6699]/30 bg-[#ff6699]/[0.07] text-sm text-white"
            data-testid="meet-trade-next"
          >
            <Calendar size={14} className="text-[#ff6699]" />
            <span className="font-semibold">Next Edition · Coming Soon</span>
          </span>
          <span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/[0.03] text-sm text-white/85"
            data-testid="meet-trade-location"
          >
            <MapPin size={14} className="text-[#ff6699]" />
            <span className="font-medium">Previous · Kuala Lumpur</span>
          </span>
        </div>
      </div>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
        {/* Poster */}
        <div className="lg:col-span-2 reveal-on-scroll">
          <div className="relative rounded-3xl overflow-hidden border border-[#ff6699]/30 shadow-[0_30px_90px_-30px_rgba(255,102,153,0.5)] transition-transform duration-700 hover:scale-[1.015]">
            <img
              src={MEET_TRADE_POSTER}
              alt="Meet & Trade — Live event with Fira, 13 October 2026, Kuala Lumpur, limited to 30 pax"
              className="w-full h-auto block"
              loading="lazy"
              data-testid="meet-trade-poster"
            />
          </div>
        </div>

        {/* Agenda + CTA */}
        <div className="lg:col-span-3">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-xl bg-[#ff6699]/15 border border-[#ff6699]/30 flex items-center justify-center text-[#ff6699]">
              <Sparkles size={16} strokeWidth={1.8} />
            </div>
            <h3 className="font-display font-semibold text-lg sm:text-xl tracking-tight">
              Agenda Malam Tu
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {MEET_TRADE_AGENDA.map((a, i) => (
              <div
                key={a.title}
                className="group rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur p-5 transition-all hover:border-[#ff6699]/40 hover:bg-[#ff6699]/[0.05] hover:-translate-y-0.5 reveal-on-scroll"
                style={{ transitionDelay: `${i * 90}ms` }}
                data-testid={`meet-trade-agenda-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#ff6699]/10 border border-[#ff6699]/30 flex items-center justify-center text-[#ff6699] group-hover:scale-110 transition-transform mb-4">
                  <a.icon size={18} strokeWidth={1.8} />
                </div>
                <div className="font-display font-semibold text-base text-white tracking-tight">
                  {a.title}
                </div>
                <p className="mt-1.5 text-xs sm:text-sm text-white/60 leading-relaxed">
                  {a.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col items-center text-center gap-4 lg:items-start lg:text-left reveal-on-scroll">
            <div className="w-full flex justify-center lg:justify-start">
              <button
                type="button"
                onClick={() => openTelegram("meet-trade")}
                className="btn-cta"
                data-testid="meet-trade-cta-button"
              >
                <span>Join Channel</span>
                <ArrowRight size={18} strokeWidth={2.4} />
              </button>
            </div>
            <p className="text-xs text-white/45 max-w-md">
              Masuk Telegram channel rasmi kami untuk dapat maklumat terawal
              bila tarikh &amp; lokasi edisi seterusnya diumumkan.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section
    id="features"
    className="relative py-24 sm:py-28 px-5 sm:px-8 overflow-hidden"
    data-testid="features-section"
  >
    {/* Section background glow */}
    <div
      className="glow-orb pointer-events-none"
      style={{
        width: 520,
        height: 520,
        top: "35%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        background: "#ff6699",
        opacity: 0.14,
      }}
    />

    <div className="relative max-w-6xl mx-auto">
      {/* Section header */}
      <div className="max-w-2xl reveal-on-scroll">
        <span className="pill">
          <Zap size={14} /> Features
        </span>
        <h2 className="font-display font-bold text-3xl sm:text-5xl mt-6 leading-tight tracking-tight">
          Fira <span className="shiny-text">Smart Desk</span>
        </h2>
        <p className="mt-5 text-white/65 text-base sm:text-lg max-w-xl leading-relaxed">
          Semua tools yang anda perlukan untuk trade dengan lebih jelas,
          tersusun dan confident — dalam satu sistem futuristic.
        </p>
      </div>

      {/* Bento: feature cards surround mockup */}
      <div className="mt-10 sm:mt-14 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-2 sm:gap-4 lg:gap-8 items-center">
        {/* Left column — feature 1 + 3 (desktop) */}
        <div className="lg:col-span-3 flex flex-col gap-4 sm:gap-6">
          <FeatureCard
            f={FEATURES[0]}
            index={0}
            revealClass="reveal-from-left"
            data-testid="feature-card-0"
          />
          <FeatureCard
            f={FEATURES[2]}
            index={2}
            revealClass="reveal-from-left"
            hideOnMobile
            data-testid="feature-card-2"
          />
        </div>

        {/* Center — phone mockup */}
        <div className="lg:col-span-6 relative flex items-center justify-center order-first lg:order-none -my-2 sm:-my-4 lg:my-0">
          {/* Pulsing pink glow behind mockup */}
          <div
            aria-hidden="true"
            className="mockup-glow-pulse absolute top-1/2 left-1/2 -z-10"
            style={{
              width: "min(85%, 460px)",
              aspectRatio: "1 / 1",
              background:
                "radial-gradient(circle at 50% 45%, rgba(255,102,153,0.55) 0%, rgba(255,102,153,0.12) 45%, transparent 70%)",
              filter: "blur(50px)",
              borderRadius: "50%",
            }}
          />

          {/* Mockup image */}
          <div className="relative w-full max-w-[300px] sm:max-w-[380px] lg:max-w-none mockup-float">
            <img
              src={PHONE_MOCKUP}
              alt="Fira Smart Desk — AI trading dashboard on mobile"
              loading="lazy"
              className="w-full h-auto block select-none pointer-events-none"
              data-testid="features-phone-mockup"
              draggable={false}
            />
          </div>
        </div>

        {/* Right column — feature 2 + 4 (desktop) */}
        <div className="lg:col-span-3 flex flex-col gap-4 sm:gap-6">
          <FeatureCard
            f={FEATURES[1]}
            index={1}
            revealClass="reveal-from-right"
            data-testid="feature-card-1"
          />
          <FeatureCard
            f={FEATURES[3]}
            index={3}
            revealClass="reveal-from-right"
            data-testid="feature-card-3"
          />
        </div>

        {/* Feature 3 on mobile only — inserted after mockup */}
        <div className="lg:hidden">
          <FeatureCard
            f={FEATURES[2]}
            index={2}
            revealClass="reveal-on-scroll"
            data-testid="feature-card-2-mobile"
          />
        </div>
      </div>

      <div className="mt-14 flex justify-center reveal-on-scroll">
        <CtaButton
          label="Join Telegram"
          source="features"
          testId="features-cta-button"
        />
      </div>
    </div>
  </section>
);

const FeatureCard = ({ f, index, revealClass, hideOnMobile, ...rest }) => (
  <div
    className={`feature-card ${revealClass}${hideOnMobile ? " hidden lg:block" : ""}`}
    style={{ transitionDelay: `${index * 90}ms` }}
    {...rest}
  >
    <div className="feature-icon-wrap">{f.icon}</div>
    <h3 className="font-display font-semibold text-xl text-white tracking-tight">
      {f.title}
    </h3>
    <p className="mt-3 text-white/60 text-sm leading-relaxed">{f.desc}</p>
    <div className="mt-6 flex items-center gap-2 text-[#ff6699] text-xs font-medium uppercase tracking-wider">
      <span className="h-px w-8 bg-[#ff6699]/40" />
      Smart Desk
    </div>
  </div>
);

const Testimoni = () => (
  <section
    className="relative py-28 px-5 sm:px-8 overflow-hidden"
    data-testid="testimoni-section"
  >
    <div
      className="glow-orb"
      style={{
        width: 380,
        height: 380,
        top: 40,
        right: -120,
        background: "#ff6699",
        opacity: 0.25,
      }}
    />
    <div className="max-w-6xl mx-auto">
      <div className="text-center max-w-3xl mx-auto reveal-on-scroll">
        <span className="pill">
          <ShieldCheck size={14} /> Testimoni
        </span>
        <h2 className="font-display font-bold text-3xl sm:text-5xl mt-6 leading-tight tracking-tight">
          Dipercayai Trader Yang Mahu{" "}
          <span className="headline-accent">
            Lebih Dari Sekadar Signal
          </span>
        </h2>
        <p className="mt-5 text-white/65 text-base sm:text-lg leading-relaxed">
          Lihat sendiri bagaimana Smart Desk bantu trader trade dengan lebih
          jelas, tersusun dan confident.
        </p>
      </div>

      <div className="mt-14 relative reveal-on-scroll">
        <Carousel
          opts={{ align: "center", loop: true }}
          className="w-full max-w-xl mx-auto md:max-w-sm"
        >
          <CarouselContent>
            {TESTIMONIALS.map((t, idx) => (
              <CarouselItem
                key={idx}
                className="basis-full"
                data-testid={`testi-slide-${idx}`}
              >
                <div className="testi-frame">
                  <img
                    src={t.src}
                    alt={t.alt}
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious
            className="bg-[#ff6699] text-black border-none hover:bg-[#ff7aa6] hover:text-black -left-4 sm:-left-12"
            data-testid="testi-prev"
          />
          <CarouselNext
            className="bg-[#ff6699] text-black border-none hover:bg-[#ff7aa6] hover:text-black -right-4 sm:-right-12"
            data-testid="testi-next"
          />
        </Carousel>
      </div>

      <div className="mt-14 flex justify-center">
        <CtaButton
          label="Join Telegram"
          source="testimoni"
          testId="testimoni-cta-button"
        />
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section
    className="relative py-28 px-5 sm:px-8 overflow-hidden"
    data-testid="final-cta-section"
  >
    <div
      className="glow-orb"
      style={{
        width: 560,
        height: 560,
        top: "20%",
        left: "50%",
        transform: "translateX(-50%)",
        background: "#ff6699",
        opacity: 0.35,
      }}
    />
    <div className="absolute inset-0 grid-bg" />

    <div className="relative max-w-4xl mx-auto text-center">
      <div className="reveal-on-scroll">
        <span className="pill" data-testid="final-promo-pill">
          <Sparkles size={14} /> Akses Penuh
        </span>

        <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-7 tracking-tight">
          Mulakan Trade Lebih Smart Dengan{" "}
          <span className="headline-accent">Fira Smart Desk</span>
        </h2>
        <p className="mt-6 text-white/70 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
          Sertai komuniti trader yang trade dengan lebih jelas, tersusun dan
          confident. Akses penuh ke AI trading system, weekly education dan 24/5
          support.
        </p>
      </div>

      <div className="mt-10 flex flex-col items-center gap-4 reveal-on-scroll">
        <CtaButton
          label="Join Telegram"
          source="final"
          testId="final-cta-button"
        />
        <p className="text-xs text-white/45 max-w-md">
          Anda akan dialihkan ke Telegram group rasmi Fira Smart Desk untuk
          dapatkan akses penuh.
        </p>
      </div>

      {/* benefits */}
      <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 max-w-3xl mx-auto">
        {[
          "FREE Fira Smart Desk",
          "FREE Fira Indicator",
          "FREE Daily Mapping",
          "Weekly Advance Education",
          "Exclusive Community Access",
          "24/5 Live Support",
        ].map((b, i) => (
          <div
            key={b}
            className="rounded-full border border-[#ff6699]/30 bg-[#ff6699]/[0.06] px-4 py-2.5 text-xs sm:text-sm font-medium text-white/85 flex items-center gap-2 reveal-on-scroll transition-colors hover:border-[#ff6699] hover:bg-[#ff6699]/10"
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff6699]" />
            {b}
          </div>
        ))}
      </div>
    </div>
  </section>
);


const SOCIAL_PROOF_NAMES = [
  "Ahmad", "Ali", "Aiman", "Aina", "Amirul", "Ashraf", "Aisyah",
  "Danish", "Danial", "Faris", "Farhan", "Farah", "Hana", "Hafiz",
  "Iman", "Irfan", "Izzah", "Khalis", "Muiz", "Nabil", "Nadia",
  "Naim", "Naufal", "Nisa", "Norman", "Nurul", "Rahim", "Ridwan",
  "Sofea", "Syafiq", "Syazwan", "Umar", "Wafiy", "Wani", "Yusuf",
  "Zahra", "Zulaikha", "Adam", "Afiq", "Balqis", "Hakim", "Haziq",
  "Imran", "Luqman", "Mikail", "Rania", "Sarah", "Zaki",
];

const SocialProofNotifier = () => {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [minAgo, setMinAgo] = useState(2);

  useEffect(() => {
    const hideDelayMs = 2000;
    const cycleMs = 8000;
    let hideTimer;

    const showOne = () => {
      setIdx((i) => (i + 1) % SOCIAL_PROOF_NAMES.length);
      setMinAgo(Math.floor(Math.random() * 24) + 1);
      setVisible(true);
      hideTimer = setTimeout(() => setVisible(false), hideDelayMs);
    };

    const initial = setTimeout(showOne, 3500);
    const cycle = setInterval(showOne, cycleMs);
    return () => {
      clearTimeout(initial);
      clearTimeout(hideTimer);
      clearInterval(cycle);
    };
  }, []);

  const name = SOCIAL_PROOF_NAMES[idx];

  return (
    <div
      className={`fixed z-[60] left-3 right-3 bottom-3 sm:left-5 sm:right-auto sm:bottom-5 transition-all duration-500 ease-out ${
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      role="status"
      aria-live="polite"
      data-testid="social-proof-notif"
    >
      <div className="flex items-center gap-3.5 sm:gap-3 pl-3.5 pr-5 sm:pl-3 sm:pr-4 py-3 sm:py-2.5 rounded-2xl bg-black/85 backdrop-blur-xl border border-[#ff6699]/25 shadow-[0_20px_50px_-15px_rgba(255,102,153,0.45)] sm:max-w-md">
        <div className="w-11 h-11 sm:w-10 sm:h-10 shrink-0 rounded-full bg-gradient-to-br from-[#ff6699] to-[#ff3d7f] flex items-center justify-center text-white font-bold text-base sm:text-sm shadow-[0_0_16px_-4px_rgba(255,102,153,0.6)]">
          {name[0]}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm sm:text-sm leading-snug">
            <span className="font-semibold text-white">{name}</span>{" "}
            <span className="text-white/70">telah mendapatkan Smart Desk</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1 sm:mt-0.5 text-[11px] sm:text-[10px] text-white/50">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
              <span className="relative rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            <span>{minAgo} min ago</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = () => (
  <footer
    className="border-t border-white/5 px-5 sm:px-8 py-10 text-white/50 text-sm"
    data-testid="site-footer"
  >
    <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-5">
      <div className="flex items-center gap-3">
        <img
          src={LOGO_BLACK}
          alt="Fira"
          className="logo-invert h-7 w-7 object-contain"
        />
        <span className="text-white/70 font-medium">
          Fira Smart Desk · © {new Date().getFullYear()}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <TrendingUp size={14} className="text-[#ff6699]" />
        <span>Trade smart. Trade with Fira.</span>
      </div>
    </div>
  </footer>
);

const HERO_V2_VIDEO =
  "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260328_105406_16f4600d-7a92-4292-b96e-b19156c7830a.mp4";

const HeroV2 = () => {
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Force muted (DOM property — some browsers ignore JSX attribute)
    v.muted = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");

    const onPlaying = () => setVideoPlaying(true);
    const onPause = () => setVideoPlaying(false);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("pause", onPause);

    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") {
        // Silently ignore — if autoplay blocked, just show black bg (no play button)
        p.catch(() => {});
      }
    };

    tryPlay();
    v.addEventListener("canplay", tryPlay);
    v.addEventListener("loadeddata", tryPlay);

    return () => {
      v.removeEventListener("canplay", tryPlay);
      v.removeEventListener("loadeddata", tryPlay);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("pause", onPause);
    };
  }, []);

  return (
  <section
    className="relative w-full min-h-screen bg-black overflow-hidden"
    data-testid="hero-v2-section"
  >
    {/* Background video — invisible until actually playing (prevents native play button overlay) */}
    <video
      ref={videoRef}
      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
      style={{ opacity: videoPlaying ? 1 : 0 }}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      controls={false}
      disablePictureInPicture
      aria-hidden="true"
      data-testid="hero-v2-video"
    >
      <source src={HERO_V2_VIDEO} type="video/mp4" />
    </video>
    {/* Subtle dark scrim for text legibility */}
    <div
      className="absolute inset-0 bg-black/40"
      aria-hidden="true"
    />
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(180deg, rgba(8,8,10,0.55) 0%, rgba(8,8,10,0.15) 35%, rgba(8,8,10,0.15) 65%, rgba(8,8,10,0.85) 100%)",
      }}
      aria-hidden="true"
    />

    {/* Content layer */}
    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 sm:pt-32 pb-16 min-h-screen flex flex-col">
      {/* Contest badge — centered */}
      <div
        className="flex justify-center hero-anim"
        style={{ animationDelay: "0.05s" }}
        data-testid="hero-v2-top"
      >
        <div
          className="group inline-flex items-center gap-2.5 px-5 py-2 rounded-full bg-[#ff6699]/[0.1] border border-[#ff6699]/40 backdrop-blur-md text-white/95 text-xs sm:text-sm font-medium tracking-tight shadow-[0_8px_30px_-12px_rgba(255,102,153,0.4)]"
          data-testid="hero-v2-promo-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inset-0 rounded-full bg-[#ff6699] animate-ping opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ff6699]" />
          </span>
          <span className="font-bold text-[#ff6699] tracking-wider uppercase text-[10px] sm:text-xs">
            Fira Smart Desk is Live
          </span>
        </div>
      </div>

      {/* Center hero */}
      <div className="flex-1 flex flex-col justify-center items-center text-center mt-14 lg:mt-0">
        <h1
          className="font-display font-semibold tracking-tighter leading-[0.9] text-5xl sm:text-7xl md:text-8xl xl:text-9xl text-white"
          data-testid="hero-v2-headline"
        >
          <span
            className="block hero-anim"
            style={{ animationDelay: "0.2s" }}
          >
            Smarter Analysis,
          </span>
          <span
            className="block hero-anim"
            style={{ animationDelay: "0.35s" }}
          >
            <span className="shiny-text">Sharper Entries</span>
          </span>
        </h1>

        <p
          className="mt-6 sm:mt-7 text-white/70 text-sm sm:text-base md:text-lg leading-relaxed tracking-tight max-w-xl font-light hero-anim"
          style={{ animationDelay: "0.55s" }}
          data-testid="hero-v2-pretitle"
        >
          AI-powered trading system untuk bantu anda trade
        </p>

        <button
          type="button"
          onClick={() => openTelegram("hero-v2")}
          className="btn-cta mt-9 hero-anim"
          style={{ animationDelay: "0.7s" }}
          data-testid="hero-v2-cta-button"
        >
          <span>Join Telegram</span>
          <ArrowRight size={18} strokeWidth={2.4} />
        </button>

        {/* Rating badge */}
        <div
          className="mt-6 flex items-center gap-2.5 hero-anim"
          style={{ animationDelay: "0.78s" }}
          data-testid="hero-v2-rating"
        >
          <div
            className="flex items-center gap-0.5"
            aria-label="4.2 out of 5 stars"
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                size={15}
                strokeWidth={0}
                className="fill-[#ff6699] text-[#ff6699]"
              />
            ))}
          </div>
          <span className="font-semibold text-white text-sm tabular-nums">
            4.2/5
          </span>
          <span className="text-white/45 text-xs sm:text-sm">
            · Trusted by 60,000+ traders
          </span>
        </div>

        {/* Stats strip */}
        <div className="mt-12 grid grid-cols-3 w-full max-w-3xl gap-3 sm:gap-6">
          {[
            {
              id: "students",
              num: { end: 60000, suffix: "+" },
              label: "Trained Students",
              delay: 850,
            },
            {
              id: "hours",
              num: { end: 500, suffix: "+" },
              label: "Teaching Hours",
              delay: 950,
            },
            {
              id: "countries",
              num: { end: 8, suffix: "" },
              label: "Countries Reached",
              delay: 1050,
            },
          ].map((s) => (
            <div
              key={s.id}
              className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md py-4 sm:py-5 px-3 hero-anim transition-colors hover:border-[#ff6699]/30 hover:bg-[#ff6699]/[0.05]"
              style={{ animationDelay: `${s.delay}ms` }}
              data-testid={`hero-v2-stat-${s.id}`}
            >
              <CountUp
                end={s.num.end}
                duration={2200}
                suffix={s.num.suffix}
                className="block text-2xl sm:text-3xl font-bold text-[#ff6699] tabular-nums"
              />
              <div className="text-[10px] sm:text-xs text-white/55 mt-1 tracking-[0.15em] uppercase">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};

const InAppBrowserBanner = () => {
  const [env] = useState(() => detectEnv());
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.sessionStorage.getItem("fira_inapp_dismissed") === "1";
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (env.inApp && !dismissed) {
      document.body.classList.add("has-inapp-banner");
    } else {
      document.body.classList.remove("has-inapp-banner");
    }
    return () => document.body.classList.remove("has-inapp-banner");
  }, [env.inApp, dismissed]);

  if (!env.inApp || dismissed) return null;

  const onClose = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem("fira_inapp_dismissed", "1");
    } catch (e) {
      // ignore
    }
  };

  const instruction =
    env.os === "ios" ? (
      <>
        Tap{" "}
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-white/15 mx-0.5 align-middle">
          <MoreHorizontal size={12} strokeWidth={2.5} />
        </span>{" "}
        di bawah → <strong className="font-semibold">Open in Safari</strong>
      </>
    ) : env.os === "android" ? (
      <>
        Tap{" "}
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-white/15 mx-0.5 align-middle">
          <MoreVertical size={12} strokeWidth={2.5} />
        </span>{" "}
        di atas →{" "}
        <strong className="font-semibold">Open in External Browser</strong>
      </>
    ) : (
      <>
        Buka link ini dalam{" "}
        <strong className="font-semibold">Chrome atau Safari</strong> untuk
        pengalaman terbaik
      </>
    );

  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] bg-[#ff6699] text-black shadow-[0_8px_30px_-12px_rgba(255,102,153,0.6)]"
      data-testid="inapp-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-8 flex items-start gap-3">
        <AlertCircle
          size={20}
          strokeWidth={2.2}
          className="flex-shrink-0 mt-0.5"
        />
        <div className="flex-1 text-[13px] sm:text-sm leading-snug">
          <div className="font-bold text-[13px] sm:text-sm">
            Anda dalam {env.source} browser
          </div>
          <div className="mt-0.5 text-black/85">{instruction}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Tutup amaran"
          data-testid="inapp-banner-close"
          className="flex-shrink-0 p-1.5 rounded-full hover:bg-black/10 transition-colors"
        >
          <X size={18} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
};

function Home() {
  useEffect(() => {
    // Track page view once on mount
    trackEvent("pageview");

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return;
    }
    const els = document.querySelectorAll(
      ".reveal-on-scroll, .reveal-from-left, .reveal-from-right",
    );
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="App" data-testid="home-page">
      <InAppBrowserBanner />
      <Nav />
      <HeroV2 />
      <Tape />
      <MeetTrade />
      <div className="divider-line max-w-5xl mx-auto" />
      <Features />
      <div className="divider-line max-w-5xl mx-auto" />
      <Testimoni />
      <div className="divider-line max-w-5xl mx-auto" />
      <FinalCta />
      <Footer />
      <SocialProofNotifier />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}
